import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, X } from 'lucide-react';
import {
  DRAFT_PREFIX, getDraftItem, removeDraftItem, listDraftKeys,
} from '../utils/draftStorage';

const DRAFT_META = {
  new_task:           { label: 'Task',       path: '/',          field: 'title' },
  new_reading:        { label: 'Reading',    path: '/reading',   field: 'url' },
  new_payment:        { label: 'Payment',    path: '/payments',  field: 'title' },
  new_call:           { label: 'Call',       path: '/calls',     field: 'contactName' },
  new_template:       { label: 'Template',   path: '/templates', field: 'name' },
  followup:           { label: 'Follow-up',  path: '/',          field: 'title' },
  review_reflection:  { label: 'Reflection', path: '/review',    field: 'wentWell' },
};

function getDrafts() {
  const drafts = [];
  try {
    listDraftKeys().forEach((key) => {
      const suffix = key.slice(DRAFT_PREFIX.length);
      const meta = DRAFT_META[suffix];
      if (!meta) return;
      const rawStr = getDraftItem(key);
      if (!rawStr) return;
      const raw = JSON.parse(rawStr);
      if (!raw?.data) return;
      const preview = raw.data[meta.field] || '';
      drafts.push({ key: suffix, label: meta.label, path: meta.path, preview: preview.slice(0, 30) });
    });
  } catch { /* storage unavailable */ }
  return drafts;
}

export function DraftIndicator({ expanded }) {
  const [drafts, setDrafts] = useState(getDrafts);
  const navigate = useNavigate();

  useEffect(() => {
    // Re-scan on storage events (cross-tab) and on a short interval (same-tab)
    const refresh = () => setDrafts(getDrafts());
    window.addEventListener('storage', refresh);
    const interval = setInterval(refresh, 3000);
    return () => { window.removeEventListener('storage', refresh); clearInterval(interval); };
  }, []);

  if (drafts.length === 0) return null;

  const handleClear = (suffix, e) => {
    e.stopPropagation();
    removeDraftItem(`${DRAFT_PREFIX}${suffix}`);
    setDrafts(getDrafts());
  };

  if (!expanded) {
    return (
      <div
        data-testid="draft-indicator-collapsed"
        className="flex items-center justify-center mb-2 relative cursor-pointer"
        title={`${drafts.length} unsaved draft${drafts.length > 1 ? 's' : ''}`}
        onClick={() => navigate(drafts[0].path)}
      >
        <FileText className="w-4 h-4" style={{ color: 'var(--pd-focus)' }} />
        <span
          className="absolute -top-1 -right-1 min-w-[14px] h-[14px] rounded-full flex items-center justify-center text-[8px] font-bold text-white"
          style={{ background: 'var(--pd-focus)', lineHeight: 1 }}
        >
          {drafts.length}
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="draft-indicator"
      className="rounded-2xl px-3 py-2.5 mb-2"
      style={{ background: 'var(--pd-blue-soft)', border: '1px solid rgba(37,99,235,0.12)' }}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <FileText className="w-3.5 h-3.5" style={{ color: 'var(--pd-focus)' }} />
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--pd-focus)' }}>
          {drafts.length} draft{drafts.length > 1 ? 's' : ''}
        </span>
      </div>
      <div className="space-y-1">
        {drafts.map((d) => (
          <div
            key={d.key}
            data-testid={`draft-item-${d.key}`}
            className="flex items-center gap-2 group cursor-pointer rounded-lg px-2 py-1.5 transition-colors"
            style={{ background: 'transparent' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(37,99,235,0.06)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            onClick={() => navigate(d.path)}
          >
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold" style={{ color: 'var(--pd-text)' }}>{d.label}</div>
              {d.preview && (
                <div className="text-[9px] truncate" style={{ color: 'var(--pd-muted)' }}>{d.preview}</div>
              )}
            </div>
            <button
              data-testid={`draft-clear-${d.key}`}
              onClick={(e) => handleClear(d.key, e)}
              className="opacity-0 group-hover:opacity-100 p-0.5 rounded transition-opacity"
              title="Discard draft"
            >
              <X className="w-3 h-3" style={{ color: 'var(--pd-muted)' }} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
